import { useNavigate } from 'react-router-dom'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAnalytics } from '../../hooks/useAnalytics/useAnalytics'
import { useMatchingBudget } from '../../hooks/useMatchingBudget/useMatchingBudget'
import { usePermission } from '../../hooks/usePermission/usePermission'
import { permission, scope } from '../../hoc/withPermissions'

import { Permissions } from '@percent/workplace-giving/constants/permissions'
import { Icon, Menu } from '@percent/lemonade'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import classes from './OrganisationCardActions.module.scss'
import { Organisation } from '@percent/workplace-giving/api/featured/featured.types'
import {
  OrgAllowlistScope,
  OrgSearchFor
} from '@percent/workplace-giving/api/search/searchOrganisations/searchVerifiedOrganisations.types'

type OrganisationCardActionsProps = Readonly<{
  organisation: Organisation
  ab488Compliant?: boolean
  onChange?: (isOpen: boolean) => void
  donationsNotAllowed: boolean
  allowlistScopes?: OrgAllowlistScope[]
}>

export function OrganisationCardActions({
  organisation,
  ab488Compliant,
  onChange,
  donationsNotAllowed,
  allowlistScopes
}: OrganisationCardActionsProps) {
  const { track } = useAnalytics()
  const navigation = useNavigate()
  const { t } = useTranslation()
  const has = usePermission()
  const { data: matching } = useMatchingBudget()

  const isAllowlisted = (allowlistScopes?.length ?? 0) > 0
  const donationAllowed = !isAllowlisted || allowlistScopes!.includes(OrgSearchFor.Donation)
  const volunteeringAllowed = !isAllowlisted || allowlistScopes!.includes(OrgSearchFor.Volunteering)

  const searchParamsFromOrg = new URLSearchParams({
    organisationId: organisation.id
  }).toString()

  const availableActions = useMemo(
    () => [
      ...(ab488Compliant !== false &&
      donationAllowed &&
      has(scope('donation_matching'), scope('off_platform_matching')) &&
      (matching?.remainingAmount?.amount ?? 0) > 0 &&
      !donationsNotAllowed
        ? [
            {
              key: 'submitReceipt',
              name: t('workplace_giving.navigation.submitReceipt'),
              onClick: () => {
                track({
                  event: 'Donation Match Request Start',
                  properties: {
                    origin: 'Search Result Card'
                  }
                })
                navigation(AppRoute.CREATE_MATCH_REQUEST, { state: { defaultOrg: organisation } })
              }
            }
          ]
        : []),
      ...(volunteeringAllowed && has(scope('volunteering')) && has(permission(Permissions.OpportunityWrite))
        ? [
            {
              key: 'createOpportunity',
              name: t('workplace_giving.navigation.createOpportunity'),
              onClick: () => {
                track({
                  event: 'Volunteering Opportunity Create Start',
                  properties: {
                    origin: 'Search Result Card'
                  }
                })
                navigation({
                  pathname: `${AppRoute.CREATE_OPPORTUNITY}`,
                  search: searchParamsFromOrg
                })
              }
            }
          ]
        : []),
      ...(volunteeringAllowed && has(scope('volunteering'))
        ? [
            {
              key: 'logTime',
              name: t('workplace_giving.navigation.logTime'),
              onClick: () => {
                track({
                  event: 'Volunteering Time Logging Start',
                  properties: {
                    origin: 'Search Result Card'
                  }
                })
                navigation({
                  pathname: AppRoute.LOG_VOLUNTEERING_ACTIVITY,
                  search: searchParamsFromOrg
                })
              }
            }
          ]
        : []),
      ...(ab488Compliant !== false &&
      donationAllowed &&
      has(scope('donation_link'), permission(Permissions.FundraiserWrite)) &&
      !donationsNotAllowed
        ? [
            {
              key: 'createFundraiser',
              name: t('workplace_giving.navigation.createFundraiser'),
              onClick: () => {
                track({
                  event: 'Donation Fundraiser Create Start',
                  properties: {
                    origin: 'Search Result Card'
                  }
                })
                navigation({
                  pathname: `${AppRoute.CREATE_FUNDRAISER}`,
                  search: searchParamsFromOrg
                })
              }
            }
          ]
        : [])
    ],
    [
      has,
      matching?.remainingAmount?.amount,
      navigation,
      organisation,
      searchParamsFromOrg,
      t,
      track,
      ab488Compliant,
      donationsNotAllowed,
      donationAllowed,
      volunteeringAllowed
    ]
  )

  return (
    <Menu
      onChange={isOpen => {
        onChange?.(isOpen)
      }}
      position="top-start"
      customTriggerComponent={
        <button
          className={classes.MenuBtn}
          type="button"
          title={t('workplace_giving.common.more')}
          data-testid="organisation-card-actions-menu"
        >
          <Icon name="menu-horizontal" size={4} color="neutral300" />
        </button>
      }
      onSelect={itemKey => availableActions.find(({ key }) => key === itemKey)?.onClick()}
      sections={[
        {
          items: availableActions.map(action => ({
            key: action.key,
            label: action.name
          }))
        }
      ]}
    />
  )
}
